// QuestionRenderer.jsx
import React, { useState } from "react";
import ViewEditor from "../../components/common/editor/ViewEditor";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertFromRaw } from "draft-js";
import CheckBox from "../../components/common/form/CheckBox";
import CheckBoxInline from "../../components/common/form/CheckBoxInline";
import Radio from "../../components/common/form/Radio";
import RadioInline from "../../components/common/form/RadioInline";
import SubmissionFileUpload from "../../components/common/form/SubmissionFileUpload";
import QuestionButton from "../../components/common/form/QuestionButton";
import { IoDocumentAttachOutline } from "react-icons/io5";
import { FaPencil, FaRegFile } from "react-icons/fa6";
import { questionMCQAnswerTypeConstants, questionTypeConstants } from "../../helper/constants";
import { resolveFileName, getFileExtension, getFileIconClass } from "./fileUtils.js";


const QuestionRenderer = ({
  question,
  index,
  qIndex,
  answer,
  answers,
  tempQuestionState,
  tempAnswerStates,
  makeRevise,
  makeEdit,
  markAnswer,
  markOneAnswer,
  getSubmissionContent,
  deleteSubmissionContent,
  getQuestionPassageContent,
  filesUploaded,
  subQuestions,
  sections = [],
  renderSubQuestionEditor,
  onDownloadAttachment,
  candidateId,
  containerName,
  handleAttachFileClick,
  handleFileChange,
  handleFileDownload,
  handleFileDelete,
}) => {
  const [toolbarVisible, setToolbarVisible] = useState(false);
  const renderPassage = () => {
    if (question.is_passage && question.passage_id && question.passage_content) {
      return (
        <div className="mb-5 mt-5">
          <ViewEditor editorState={getQuestionPassageContent(question.passage_content)} />
        </div>
      );
    }
    return null;
  };

  const renderReviseBox = () => {
    const employeeType = sessionStorage.getItem("employeeType");
    const userType = sessionStorage.getItem("userType");
    const isDeopOrMasad = employeeType === "DEOP" || employeeType === "MASAD" || userType === "DEOP" || userType === "MASAD";
    if (isDeopOrMasad) return null;

    return (
      <div className="pl-1 pr-1 rounded d-inline-block push-button" style={{ fontSize: "11px", border: "none" }}>
        <CheckBoxInline>
          <CheckBox
            key={qIndex}
            onClick={(e) => makeRevise(e, qIndex)}
            checked={question.needToRevise || false}
            checkBoxName="Mark for Review"
            style={{ color: "#5458AF", transform: "scale(0.85)" }}
            inputStyle={{ outline: "none" }}
          />
        </CheckBoxInline>
      </div>
    );
  };

  const renderDescriptive = () => {
    const attachedFiles = Array.isArray(answers?.[question.question_id])
      ? answers[question.question_id]
      : [];

    return (
      <div className="col-md-12" style={{ overflow: "auto" }}>
        {renderPassage()}

        {/* Question with Mark for Review on the right */}
        <div className="d-flex mb-3 mt-3 pr-0 pl-2 overflow-hidden all-questions-title">
          <div className="d-flex align-items-start" style={{ flex: 1 }}>
            <div className="number-wrap" style={{ fontSize: "12px", marginTop: "1px" }}>
              {index + 1}.
            </div>
            {question.is_optional && <span className="badge badge-primary mr-2">Optional</span>}
            <div className="rdw-editor-question" style={{ flex: 1 }}>
              <ViewEditor editorState={tempQuestionState} />
            </div>
          </div>
          {renderReviseBox()}
        </div>

        {/* Answer Section */}
        <div className="RichEditor-root-descriptive-answer">
          {/* Action Buttons Row */}
          <div className="d-flex mb-3" style={{ gap: "8px" }}>
            <input
              type="file"
              id={`fileInput-${question.question_id}`}
              style={{ display: "none" }}
              onChange={(e) => handleFileChange(e, question.question_id)}
            />

            <button
              type="button"
              className="btn btn-sm exam-action-btn"
              onClick={() => handleAttachFileClick(`fileInput-${question.question_id}`)}
            >
              <IoDocumentAttachOutline style={{ fontSize: 13, marginRight: 4 }} />
              Attach files
            </button>
            <button
              type="button"
              className="btn btn-sm exam-action-btn"
              onClick={() => setToolbarVisible((v) => !v)}
            >
              <FaPencil style={{ fontSize: 11, marginRight: 4 }} />
              {toolbarVisible ? "Disable toolbar" : "Enable toolbar"}
            </button>
          </div>

          {/* Uploaded files displayed above editor */}
          {attachedFiles.length > 0 && (
            <div className="mb-2">
              {attachedFiles.map((file, idx) => {
                const fileName = resolveFileName(file);
                const fileExt = getFileExtension(fileName);
                const iconClass = getFileIconClass(fileName);
                return (
                  <div key={idx} className="uploaded-file-card d-flex align-items-center mb-2">
                    <div className={`file-icon ${iconClass}`}>
                      <FaRegFile />
                    </div>
                    <div className="file-meta flex-grow-1 ms-3">
                      <a
                        href="#!"
                        onClick={(e) => handleFileDownload(e, question.question_id, file)}
                        className="file-title"
                      >
                        {fileName || "Untitled file"}
                      </a>
                      <span className="file-subtitle">Uploaded</span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-icon delete-file-btn ms-2"
                      onClick={() => handleFileDelete(file, question.question_id)}
                      title="Delete attachment"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Text Editor */}
          <div className="editor-wrapper" style={{ position: "relative" }}>
            <Editor
              placeholder="Type your answer here..."
              editorStyle={{
                height: "120px",
                backgroundColor: "#fafafa",
                padding: "12px",
                border: "none",
                borderRadius: "0"
              }}

              editorState={answer || EditorState.createEmpty()}
              onEditorStateChange={(e) => makeEdit(e, qIndex, question.question_id)}
              toolbar={toolbarVisible ? { fontFamily: { options: ["Arial", "Georgia", "Tahoma"] } } : {}}
              toolbarClassName={toolbarVisible ? "vertical-toolbar" : "hide-toolbar"}
            />
          </div>
        </div>

        <div className="separator separator-dashed separator-border-2 mt-5 mb-5" />
      </div>
    );
  };

  const renderSubmission = () => {
    let submissionList = answers[question.question_id] || [];
    let reload = submissionList.length > 0;

    return (
      <div className="col-md-12" style={{ overflow: "auto" }}>
        {renderPassage()}
        <h3 className="mb-5 overflow-hidden all-questions-title">
          <div className="d-flex">
            <div className="number-wrap" style={{ fontSize: "12px", marginTop: "3px" }}>{index + 1}.</div>
            {Array.isArray(sections) && sections.length > 0 && (
              <div>
                {question.is_optional && (
                  <span className="badge badge-primary mr-2">Optional</span>
                )}
              </div>
            )}
            <ViewEditor editorState={tempQuestionState} />
          </div>
          {renderReviseBox()}
        </h3>

        {/* Upload section – placed BELOW Mark for Review */}
        <div className="mt-3 ml-4">
          <SubmissionFileUpload
            className="col-md-10 col-form-label"
            key={qIndex}
            id={question.question_id}
            title={submissionList}
            defaultFile={submissionList}
            callBack={getSubmissionContent}
            deleteback={deleteSubmissionContent}
            reload={reload}
            questionIndex={question.question_id}
          />
        </div>

        <div className="separator separator-dashed separator-border-2 mt-5 mb-5" />
      </div>
    );
  };

  const renderMCQ = () => (
    <div className="col-md-12 px-0" style={{ overflow: "auto" }}>
      {renderPassage()}
      <div className="mb-2 mt-4 pl-4 pr-4 overflow-hidden all-questions-title">
        <div className="d-flex">
          <div className="number-wrap" style={{ fontSize: "12px", marginTop: "3px" }}>{index + 1}.</div>
          {Array.isArray(sections) && sections.length > 0 && (
            <div>
              {question.is_optional && (
                <span className="badge badge-primary mr-2">Optional</span>
              )}
            </div>
          )}
          <div className="rdw-editor-question">
            <ViewEditor editorState={tempQuestionState} />
          </div>
        </div>
        {renderReviseBox()}
      </div>
      <div className={`RichEditor-root px-0 parent-class ${tempAnswerStates.some(answer => answer.getCurrentContent().getPlainText().length > 100) ? "single-column" : ""}`}>
        {tempAnswerStates.map((answerState, j) => (
          <div className="row d-flex align-items-center child-class" key={j}>
            <div className="col-md-2 col-2">
              {question.answer_type_id === questionMCQAnswerTypeConstants.One.id ? (
                <RadioInline>
                  <Radio
                    key={j}
                    onChange={(e) => markOneAnswer(e, question.question_id, j)}
                    checked={answers[question.question_id]?.[0] === j}
                    style={{ color: "#5458AF" }}
                    value={j}
                  />
                </RadioInline>
              ) : (
                <CheckBoxInline>
                  <CheckBox
                    key={j}
                    onClick={(e) => markAnswer(e, question.question_id, j)}
                    checked={answers[question.question_id]?.includes(j)}
                    style={{ color: "#5458AF" }}
                  />
                </CheckBoxInline>
              )}
            </div>
            <div className="col-md-10 col-10">
              <Editor
                editorState={answerState}
                toolbarClassName="hide-toolbar"
                readOnly={true}
                toolbar={{}}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="separator separator-dashed separator-border-2 mt-3 mb-3" />
    </div>
  );

  if (question.question_type === questionTypeConstants.DESCRIPTIVE.name) return renderDescriptive();
  if (question.question_type === questionTypeConstants.MCQ.name) return renderMCQ();
  if (question.question_type === questionTypeConstants.SUBMISSION.name) {
    return (
      renderDescriptive()
    );
  }
  if (question.question_type === questionTypeConstants.SUBQUESTION.name) return renderSubQuestionEditor();

  return null;
};

export default QuestionRenderer;
